/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useCallback } from 'react';
import cn from 'classnames';
import { NavLink, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  Euro,
  Wallet,
  LocalOffer,
  Document,
  Dashboard,
  Subject,
  ArrowBarRight,
  SupervisorAccount,
  Mail,
} from 'assets/icons/sidebar';
import { ReactComponent as WrenchIcon } from 'assets/icons/wrench.svg';
import { Visible } from 'components/common/Grid';
import useLastProjectId, { LAST_PROJECT_ID_QUERY } from 'hooks/project/useLastProjectId';
import { useApolloClient } from '@apollo/react-hooks';
import { useAppContext } from 'contexts/AppContext';
import routePaths, { routePatterns } from 'router/route-paths';
import { DEVELOPER, LOAN_CONSULTANT } from 'constants/constants';
import useUser from 'hooks/user/useUser';
import { get } from 'lodash';
import s from './Sidebar.module.scss';

export const sidebarItems = [
  {
    id: '0',
    getPath: () => routePaths.myProjects,
    title: 'page_titles.my_projects',
    hideWhenNoProject: false,
    getIcon: ({ t }) => <Subject title={t({ id: 'alt_text.icons.subject' })} />,
  },
  {
    id: '1',
    getPath: ({ projectId }) => routePatterns.dashboard.stringify({ id: projectId }),
    title: 'page_titles.dashboard',
    hideWhenNoProject: true,
    getIcon: ({ t }) => <Dashboard title={t({ id: 'alt_text.icons.dashboard' })} />,
  },
  {
    id: '2',
    getPath: ({ projectId }) => routePatterns.projectDetailsCalculator.stringify({ id: projectId }),
    title: 'page_titles.project_info',
    hideWhenNoProject: true,
    getIcon: ({ t }) => <Document title={t({ id: 'alt_text.icons.document' })} />,
  },
  {
    id: '4',
    getPath: ({ projectId }) => routePatterns.plan.stringify({ id: projectId }),
    title: 'page_titles.plan',
    hideWhenNoProject: true,
    getIcon: ({ t }) => <LocalOffer title={t({ id: 'alt_text.icons.local_offer' })} />,
  },
  {
    id: '5',
    getPath: ({ projectId }) =>
      projectId
        ? routePatterns.lifestyleOverviewWithProject.stringify({ id: projectId })
        : routePaths.lifestyleOverview,
    title: 'page_titles.budget',
    hideWhenNoProject: false,
    getIcon: ({ t }) => <Wallet title={t({ id: 'alt_text.icons.wallet' })} />,
  },
  {
    id: '6',
    getPath: ({ projectId }) => routePatterns.loan.stringify({ id: projectId }),
    title: 'page_titles.loan',
    hideWhenNoProject: true,
    getIcon: ({ t }) => <Euro title={t({ id: 'alt_text.icons.euro' })} />,
  },
  {
    id: '7',
    getPath: () => routePaths.inviteUser,
    title: 'page_titles.invite_user',
    hideWhenNoProject: false,
    showFor: LOAN_CONSULTANT,
    getIcon: ({ t }) => <Mail title={t({ id: 'alt_text.icons.message' })} />,
  },
  {
    id: '8',
    getPath: () => routePatterns.invitations.stringify({ page: 1 }),
    title: 'page_titles.invitations',
    hideWhenNoProject: false,
    showFor: LOAN_CONSULTANT,
    getIcon: ({ t }) => <SupervisorAccount title={t({ id: 'alt_text.icons.supervisor_account' })} />,
  },
  {
    id: '9',
    getPath: ({ projectId }) => routePatterns.debugMode.stringify({ id: projectId }),
    title: 'page_titles.debug_mode',
    hideWhenNoProject: true,
    showFor: DEVELOPER,
    getIcon: ({ t }) => <WrenchIcon title={t({ id: 'alt_text.icons.wrench' })} />,
  },
];

const footerItems = [
  {
    id: '1',
    getPath: () => routePaths.logout,
    title: 'page_titles.logout',
    getIcon: ({ t }) => <ArrowBarRight title={t({ id: 'alt_text.icons.arrow_right' })} />,
  },
];

export function SidebarMenuItem({ projectId, path, title, icon, onClick, hideWhenNoProject, t, theme }) {
  const sidebarItemClassNames = {
    primary: {
      className: s.sidebarMenuItem,
      activeClassName: s.sidebarMenuItemActive,
    },
    green: {
      className: s.greenSidebarMenuItem,
      activeClassName: s.greenSidebarMenuItemActive,
    },
  };

  if (!projectId && hideWhenNoProject) return null;

  return (
    <li>
      <NavLink
        to={path}
        className={sidebarItemClassNames[theme].className}
        onClick={onClick}
        activeClassName={sidebarItemClassNames[theme].activeClassName}
        exact
      >
        <span className={s.sidebarMenuItemIcon}>{icon}</span>
        {t({ id: title })}
      </NavLink>
    </li>
  );
}

const useLastProjectIdHandler = () => {
  const { lastProjectId } = useLastProjectId();
  const [projectId, setProjectId] = useState('');
  const { id: paramsId } = useParams();
  const client = useApolloClient();

  useEffect(() => {
    if (lastProjectId) {
      if (paramsId && lastProjectId !== paramsId) {
        client.writeQuery({
          query: LAST_PROJECT_ID_QUERY,
          data: { lastProject: { _id: paramsId, __typename: 'Project' } },
        });
      } else {
        setProjectId(lastProjectId);
      }
    }
  }, [client, lastProjectId, paramsId, setProjectId]);
  return projectId;
};

export default function Sidebar() {
  const projectId = useLastProjectIdHandler();
  const [me] = useUser();
  const roles = get(me, 'roles', []);
  const { sidebarOpen, toggleSidebar, theme } = useAppContext();
  const hideSidebar = useCallback(() => toggleSidebar(false), [toggleSidebar]);
  const { formatMessage: t } = useIntl();

  return (
    <>
      <div className={cn(s.sidebar, { [s.sidebarOpen]: sidebarOpen })}>
        <div className={s.sidebarContent}>
          <ul className={cn(s.sidebarMenu, s.mainMenu)}>
            {sidebarItems.map((item) => {
              if (item.showFor && !roles.includes(item.showFor)) return null;
              return (
                <SidebarMenuItem
                  key={item.id}
                  onClick={hideSidebar}
                  projectId={projectId}
                  path={item.getPath({ projectId })}
                  icon={item.getIcon({ t })}
                  {...{ ...item, t, theme }}
                />
              );
            })}
          </ul>
          <Visible sm xs>
            <ul className={cn(s.sidebarMenu, s.footerMenu)}>
              {footerItems.map((item) => (
                <SidebarMenuItem key={item.id} onClick={hideSidebar} path={item.getPath()} {...{ ...item, t, theme }} />
              ))}
            </ul>
          </Visible>
        </div>
      </div>
      {sidebarOpen ? (
        <Visible xs sm>
          <div onClick={hideSidebar} className={s.sidebarOverlay} />
        </Visible>
      ) : null}
    </>
  );
}
